import { Unit, Lease } from '@urbanmix-tech/shared-js';
import dayjs from 'dayjs';
import { convertPseudoDateToRealDate } from '@/helpers/common';
import { calculateLeaseLength } from '@/helpers/leaseHelpers';

/**
 * Calculated metrics for a lease
 */
export interface LeaseMetrics {
  monthlyRent: number;
  leaseLength: number;
  concessions: number;
  effectiveMonthlyRent: number;
  effectiveAnnualRent: number;
  annualRent: number;
  annualRentPerSqft: number;
  effectiveAnnualRentPerSqft: number;
  sqft: number;
  leaseStartDate: Date | null;
  leaseEndDate: Date | null;
}

/**
 * Calculate total concessions in dollar amount from a lease
 */
export function calculateTotalConcessions(lease: Lease): number {
  return Array.isArray(lease.concessions)
    ? lease.concessions.reduce(
        (sum: number, c: any) => sum + ((c.amount || 0) * lease.monthlyRent),
        0
      )
    : (lease.concessions || 0) * lease.monthlyRent;
}

/**
 * Calculate all lease metrics including effective rent after concessions
 * This is the single source of truth for lease metric calculations
 */
export function calculateLeaseMetrics(
  unit: Unit,
  lease: Lease
): LeaseMetrics | null {
  const sqft = unit.sqft || 1;

  // Get lease dates
  const leaseStartDate = convertPseudoDateToRealDate(lease.leaseStartDate as any);
  const leaseEndDate = convertPseudoDateToRealDate(lease.leaseEndDate as any);

  if (!leaseStartDate || !leaseEndDate) {
    return null;
  }

  // Calculate lease length in months using the shared helper that rounds properly
  const leaseLength = calculateLeaseLength(lease);

  // Calculate concessions
  const concessions = Math.round(calculateTotalConcessions(lease) * 100) / 100;

  // Calculate effective rent (spread concessions over lease term)
  const effectiveMonthlyRent =
    leaseLength > 0
      ? Math.round((lease.monthlyRent - concessions / leaseLength) * 100) / 100
      : lease.monthlyRent;

  const effectiveAnnualRent = effectiveMonthlyRent * 12;
  const annualRent = lease.monthlyRent * 12;

  return {
    monthlyRent: lease.monthlyRent,
    leaseLength,
    concessions,
    effectiveMonthlyRent,
    effectiveAnnualRent,
    annualRent,
    annualRentPerSqft: annualRent / sqft,
    effectiveAnnualRentPerSqft: Math.round((effectiveAnnualRent / sqft) * 100) / 100,
    sqft,
    leaseStartDate,
    leaseEndDate,
  };
}

/**
 * Calculate effective PPSF (annual rent per sqft after concessions)
 * Uses actual lease dates when available, falls back to 12 months
 */
export function calculateEffectivePPSF(
  unit: Unit,
  lease: Lease
): number {
  const sqft = unit.sqft || 1;

  // Calculate total concessions
  const totalConcessions = Array.isArray(lease.concessions)
    ? lease.concessions.reduce(
        (sum: number, c: any) => sum + ((c.amount || 0) * lease.monthlyRent),
        0
      )
    : (lease.concessions || 0) * lease.monthlyRent;

  // Get actual lease length from dates if available
  let leaseLength = 12;
  try {
    const startDate = convertPseudoDateToRealDate(lease.leaseStartDate as any);
    const endDate = convertPseudoDateToRealDate(lease.leaseEndDate as any);

    if (startDate && endDate) {
      leaseLength = dayjs(endDate).diff(dayjs(startDate), 'month');
    }
  } catch (e) {
    // Fall back to 12 months if date parsing fails
    leaseLength = 12;
  }

  // Calculate effective rent
  const effectiveMonthlyRent =
    leaseLength > 0
      ? lease.monthlyRent - totalConcessions / leaseLength
      : lease.monthlyRent;

  const effectiveAnnualRent = effectiveMonthlyRent * 12;
  return effectiveAnnualRent / sqft;
}

/**
 * Convert concession dollar amount to months of rent
 */
export function concessionsToMonths(concessionDollars: number, monthlyRent: number): number {
  if (monthlyRent <= 0) return 0;
  return Math.round((concessionDollars / monthlyRent) * 10) / 10;
}

export interface ComparableGroup {
  sameLine: Unit[];
  sameBedrooms: Unit[];
  similarSqft: Unit[];
  combined: Unit[];
}

/**
 * Get comparable units based on line, bedrooms, and square footage
 * @param selectedUnit - The unit to find comparables for
 * @param allUnits - All available units
 * @param allLeases - All available leases (for filtering units with active leases)
 * @returns Object with 4 arrays of comparable units
 */
export function getComparableUnits(
  selectedUnit: Unit,
  allUnits: Unit[],
  allLeases: Map<string, Lease>
): ComparableGroup {
  // Filter to only units with active leases AND same affordable status
  const unitsWithLeases = allUnits.filter(
    (u) => allLeases.has(u.id) && u.id !== selectedUnit.id && u.affordable === selectedUnit.affordable
  );

  // Same line (use type or line, whichever has value)
  const selectedLine = selectedUnit.type || selectedUnit.line;
  const sameLine = unitsWithLeases.filter((u) => {
    const unitLine = u.type || u.line;
    return unitLine === selectedLine;
  });

  // Same bedrooms
  const sameBedrooms = unitsWithLeases.filter((u) => u.numBedrooms === selectedUnit.numBedrooms);

  // Similar sqft (±10%)
  const sqftRange = selectedUnit.sqft * 0.1; // 10%
  const minSqft = selectedUnit.sqft - sqftRange;
  const maxSqft = selectedUnit.sqft + sqftRange;
  const similarSqft = unitsWithLeases.filter(
    (u) => u.sqft >= minSqft && u.sqft <= maxSqft
  );

  // Combined (any of the above)
  const combinedSet = new Set<string>();
  [...sameLine, ...sameBedrooms, ...similarSqft].forEach((u) =>
    combinedSet.add(u.id)
  );
  const combined = Array.from(combinedSet).map((id) =>
    unitsWithLeases.find((u) => u.id === id)!
  );

  return {
    sameLine,
    sameBedrooms,
    similarSqft,
    combined,
  };
}

/**
 * Calculate summary statistics for a group of units
 */
export interface GroupSummary {
  avgRent: number;
  minRent: number;
  maxRent: number;
  avgPPSF: number;
  minPPSF: number;
  maxPPSF: number;
  avgEffectiveRent: number;
  minEffectiveRent: number;
  maxEffectiveRent: number;
  avgEffectivePPSF: number;
  minEffectivePPSF: number;
  maxEffectivePPSF: number;
  count: number;
}

/**
 * Calculate summary stats for a group of units
 */
export function calculateGroupSummary(
  units: Unit[],
  leaseMap: Map<string, Lease>
): GroupSummary | null {
  if (units.length === 0) {
    return null;
  }

  const metrics = units
    .map((unit) => {
      const lease = leaseMap.get(unit.id);
      if (!lease) return null;

      const sqft = unit.sqft || 1;
      const annualRent = lease.monthlyRent * 12;
      const annualRentPerSqft = annualRent / sqft;

      // Calculate effective metrics
      const totalConcessions = Array.isArray(lease.concessions)
        ? lease.concessions.reduce(
            (sum: number, c: any) => sum + ((c.amount || 0) * lease.monthlyRent),
            0
          )
        : (lease.concessions || 0) * lease.monthlyRent;

      // Estimate lease length (default to 12 months if we can't calculate)
      const leaseLength = 12; // Simplified for now

      const effectiveMonthlyRent =
        leaseLength > 0
          ? lease.monthlyRent - totalConcessions / leaseLength
          : lease.monthlyRent;
      const effectiveAnnualRent = effectiveMonthlyRent * 12;
      const effectiveAnnualRentPerSqft = effectiveAnnualRent / sqft;

      return {
        rent: lease.monthlyRent,
        ppsf: annualRentPerSqft,
        effectiveRent: effectiveMonthlyRent,
        effectivePPSF: effectiveAnnualRentPerSqft,
      };
    })
    .filter(Boolean) as any[];

  if (metrics.length === 0) {
    return null;
  }

  const rents = metrics.map((m) => m.rent);
  const ppsfValues = metrics.map((m) => m.ppsf);
  const effectiveRents = metrics.map((m) => m.effectiveRent);
  const effectivePPSFs = metrics.map((m) => m.effectivePPSF);

  const avgRent = rents.reduce((a, b) => a + b, 0) / rents.length;
  const avgPPSF = ppsfValues.reduce((a, b) => a + b, 0) / ppsfValues.length;
  const avgEffectiveRent = effectiveRents.reduce((a, b) => a + b, 0) / effectiveRents.length;
  const avgEffectivePPSF = effectivePPSFs.reduce((a, b) => a + b, 0) / effectivePPSFs.length;

  return {
    avgRent: Math.round(avgRent * 100) / 100,
    minRent: Math.round(Math.min(...rents) * 100) / 100,
    maxRent: Math.round(Math.max(...rents) * 100) / 100,
    avgPPSF: Math.round(avgPPSF * 100) / 100,
    minPPSF: Math.round(Math.min(...ppsfValues) * 100) / 100,
    maxPPSF: Math.round(Math.max(...ppsfValues) * 100) / 100,
    avgEffectiveRent: Math.round(avgEffectiveRent * 100) / 100,
    minEffectiveRent: Math.round(Math.min(...effectiveRents) * 100) / 100,
    maxEffectiveRent: Math.round(Math.max(...effectiveRents) * 100) / 100,
    avgEffectivePPSF: Math.round(avgEffectivePPSF * 100) / 100,
    minEffectivePPSF: Math.round(Math.min(...effectivePPSFs) * 100) / 100,
    maxEffectivePPSF: Math.round(Math.max(...effectivePPSFs) * 100) / 100,
    count: units.length,
  };
}
